import React from 'react';
import { Sparkles } from 'lucide-react';

const WhatAreYouLookingForModal = ({ 
  isOpen, 
  onClose, 
  onQuickSearch 
}) => {
  if (!isOpen) return null;

  // "What are you looking for?" Modal Data
  const searchCategories = {
    furniture: {
      title: '🪑 Furniture',
      color: 'from-amber-500 to-orange-500',
      items: ['Chairs', 'Tables', 'Sofas', 'Desks', 'Beds', 'Cabinets', 'Shelving', 'Benches']
    },
    performance: {
      title: '🎭 Performance',
      color: 'from-purple-500 to-pink-500', 
      items: ['Costumes', 'Props', 'Instruments', 'Scripts', 'Makeup', 'Masks', 'Backdrops', 'Set Pieces']
    },
    historical: {
      title: '🏛️ Historical',
      color: 'from-green-500 to-teal-500',
      items: ['Victorian', 'Medieval', 'Renaissance', 'Modern', 'Ancient', 'Art Deco', '1950s', 'Industrial']
    },
    byColor: {
      title: '🎨 By Color',
      color: 'from-blue-500 to-cyan-500',
      items: ['Red Items', 'Blue Items', 'Green Items', 'Yellow Items', 'Black Items', 'White Items', 'Gold Items', 'Silver Items']
    },
    bySize: {
      title: '📏 By Size', 
      color: 'from-gray-500 to-slate-500',
      items: ['XS (Handheld)', 'Small', 'Medium', 'Large', 'XL (Room-sized)', 'Oversized']
    },
    byRegion: {
      title: '🌍 By Region',
      color: 'from-indigo-500 to-blue-500',
      items: ['European', 'Asian', 'American', 'African', 'Middle Eastern', 'Latin American', 'Nordic', 'Mediterranean']
    }
  };

  const handleQuickSearch = (category, item) => {
    onQuickSearch(category, item);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                What are you looking for?
              </h2>
              <p className="text-purple-100">Browse by category to quickly find the perfect props for your production</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(searchCategories).map(([key, category]) => (
              <div key={key} className="group">
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${category.color} rounded-lg p-4 mb-3 text-white`}>
                  <h3 className="text-lg font-bold">{category.title}</h3>
                </div>
                
                {/* Category Items */}
                <div className="space-y-2">
                  {category.items.map(item => (
                    <button
                      key={item}
                      onClick={() => handleQuickSearch(key, item)}
                      className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-all group-hover:shadow-sm"
                    >
                      <div className="font-medium text-gray-900">{item}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Quick Actions Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Or try these quick searches:</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {['chairs', 'tables', 'vintage', 'modern', 'red items', 'large props'].map(term => (
                  <button
                    key={term}
                    onClick={() => handleQuickSearch('quickSearches', term)}
                    className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm hover:bg-blue-200 transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatAreYouLookingForModal;